import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Grid,
  useTheme,
  useMediaQuery,
  Paper,
  Divider,
  Stack,
  Chip
} from '@mui/material';
import {
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  FiberManualRecord as DotIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  RadioButtonUnchecked as CircleIcon,
  AccountBalance as PoliticsIcon,
  AttachMoney as EconomicsIcon,
  Groups as SocioCulturalIcon,
  Security as PeaceSecurityIcon,
  People as DemographicsIcon,
  Nature as EnvironmentIcon
} from '@mui/icons-material';

// This component uses a ResizeObserver-based custom hook (useParentWidth) to make the grid layout responsive to the parent container's width, not just the viewport.

// Custom hook to observe parent width
function useParentWidth(threshold = 700) {
  const ref = useRef(null);
  const [isStacked, setIsStacked] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const node = ref.current;
    const handleResize = (entries) => {
      for (let entry of entries) {
        setIsStacked(entry.contentRect.width < threshold);
      }
    };
    const observer = new window.ResizeObserver(handleResize);
    observer.observe(node);
    // Initial check
    setIsStacked(node.offsetWidth < threshold);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isStacked];
}

// Header component for people in need
const PeopleInNeedHeader = ({ peopleInNeed }) => {
  if (!peopleInNeed) return null;

  return (
    <Box
      sx={{
        bgcolor: 'var(--color-primary)',
        p: 3,
        textAlign: 'center',
        borderRadius: '12px',
        border: '2px solid',
        borderColor: 'var(--color-primary)',
        mb: 2,
        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        position: 'relative',
        zIndex: 2,
        background: 'linear-gradient(135deg, primary.main, primary.dark)'
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontSize: '18px',
          fontWeight: 700,
          color: 'primary.contrastText',
          mb: 1.5,
          letterSpacing: '-0.5px',
          textTransform: 'uppercase'
        }}
      >
        {peopleInNeed.total || 'People in Need'}
      </Typography>
      {peopleInNeed.description && (
        <Typography
          variant="body1"
          sx={{
            fontSize: '13px',
            color: 'primary.contrastText',
            lineHeight: 1.6,
            maxWidth: '800px',
            margin: '0 auto',
            opacity: 0.95
          }}
        >
          {peopleInNeed.description}
        </Typography>
      )}
    </Box>
  );
};

// Priority section component (top row)
const PrioritySection = ({ title, items }) => {
  if (!items || items.length === 0) return null;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        height: '100%',
        minHeight: '180px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        position: 'relative',
        zIndex: 2
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'grey.50',
          p: 2.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px 8px 0 0'
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: '14px',
            fontWeight: 700,
            color: 'text.primary',
            textAlign: 'center',
            letterSpacing: '0.25px',
            textTransform: 'uppercase'
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          {items.map((item, index) => (
            <Box key={index}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 0.75,
                  lineHeight: 1.2
                }}
              >
                {index + 1}. {item.title}
              </Typography>
              {item.description && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '11px',
                    color: 'text.secondary',
                    lineHeight: 1.5,
                    display: 'block'
                  }}
                >
                  {item.description}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

// Living conditions section component (middle row)
const LivingConditionSection = ({ title, subtitle, items }) => {
  if (!items || items.length === 0) return null;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        height: '100%',
        minHeight: '200px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        position: 'relative',
        zIndex: 2
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'var(--color-primary)',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px 8px 0 0'
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: '14px',
            fontWeight: 700,
            color: 'primary.contrastText',
            textAlign: 'center',
            letterSpacing: '0.25px',
            textTransform: 'uppercase'
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="caption"
            sx={{
              fontSize: '11px',
              color: 'primary.contrastText',
              textAlign: 'center',
              display: 'block',
              fontStyle: 'italic',
              mt: 0.75,
              opacity: 0.95,
              fontWeight: 500
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          {items.map((item, index) => (
            <Box key={index}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 0.5
                }}
              >
                {index + 1}. {item.indicator || item.mechanism || item.aspect}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '11px',
                  color: 'text.secondary',
                  lineHeight: 1.4,
                  display: 'block'
                }}
              >
                {item.status || item.description}
                {item.percentage && ` (${item.percentage})`}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

// Focal issues section (central)
const FocalIssuesSection = ({ focalIssues }) => {
  if (!focalIssues || focalIssues.length === 0) return null;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '2px solid',
        borderColor: 'var(--color-primary)',
        borderRadius: '12px',
        p: 3,
        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        position: 'relative',
        zIndex: 2,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -1,
          left: -1,
          right: -1,
          bottom: -1,
          background: 'linear-gradient(45deg, transparent, rgba(25, 118, 210, 0.1), transparent)',
          borderRadius: '12px',
          zIndex: -1
        }
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontSize: '16px',
          fontWeight: 700,
          color: 'var(--color-primary)',
          textAlign: 'center',
          mb: 2.5,
          letterSpacing: '0.5px',
          textTransform: 'uppercase'
        }}
      >
        Focal Issues
      </Typography>

      <Grid container spacing={2}>
        {focalIssues.map((issue, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <Typography
              variant="body2"
              sx={{
                fontSize: '13px',
                fontWeight: 700,
                color: 'text.primary',
                mb: 0.75,
                lineHeight: 1.3
              }}
            >
              {index + 1}. {issue.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '11px',
                color: 'text.secondary',
                lineHeight: 1.5,
                fontWeight: 400
              }}
            >
              {issue.description}
            </Typography>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// Impact section component (bottom row)
const ImpactSection = ({ title, items }) => {
  if (!items || items.length === 0) return null;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        height: '100%',
        minHeight: '160px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        position: 'relative',
        zIndex: 2
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'error.main',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px 8px 0 0'
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: '14px',
            fontWeight: 700,
            color: 'error.contrastText',
            textAlign: 'center',
            textTransform: 'uppercase'
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          {items.map((item, index) => (
            <Box key={index}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 0.5
                }}
              >
                {index + 1}. {item.title}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '11px',
                  color: 'text.secondary',
                  lineHeight: 1.4,
                  display: 'block'
                }}
              >
                {item.description}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

// Timeline section
const TimelineSection = ({ mainShocks, isStacked }) => {
  if (!mainShocks || mainShocks.length === 0) return null;

  // Helper function to sort shocks in descending order (most recent first)
  const sortShocksDescending = (shocks) => {
    return [...shocks].sort((a, b) => {
      // Try to parse dates from the period field
      const dateA = new Date(a.period);
      const dateB = new Date(b.period);
      
      // If both dates are valid, sort by date
      if (!isNaN(dateA) && !isNaN(dateB)) {
        return dateB - dateA; // Descending order (newest first)
      }
      
      // If dates can't be parsed, try to extract year
      const yearA = a.period.match(/\d{4}/);
      const yearB = b.period.match(/\d{4}/);
      
      if (yearA && yearB) {
        return parseInt(yearB[0]) - parseInt(yearA[0]); // Descending order
      }
      
      // If no dates found, maintain original order
      return 0;
    });
  };

  const sortedShocks = sortShocksDescending(mainShocks);

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        p: 2.5,
        mt: '5px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        position: 'relative',
        zIndex: 2
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontSize: '14px',
          fontWeight: 700,
          color: 'text.primary',
          textAlign: 'center',
          mb: 2.5,
          letterSpacing: '0.25px'
        }}
      >
        Main Shocks or Events
      </Typography>

      {isStacked ? (
        <Box sx={{ position: 'relative', pl: 3 }}>
          {/* Vertical timeline line */}
          <Box
            sx={{
              position: 'absolute',
              left: '12px',
              top: '8px',
              bottom: '8px',
              width: '2px',
              bgcolor: 'var(--color-primary)',
              opacity: 0.3
            }}
          />
          
          <Stack spacing={3}>
            {sortedShocks.map((shock, index) => (
              <Box
                key={index}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2
                }}
              >
                {/* Timeline dot */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: '-15px',
                    top: '2px',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    bgcolor: 'var(--color-primary)',
                    border: '2px solid',
                    borderColor: 'background.paper',
                    boxShadow: '0 0 0 2px',
                    boxShadowColor: 'var(--color-primary)'
                  }}
                />
                
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '13px',
                      fontWeight: 700,
                      color: 'var(--color-primary)',
                      mb: 0.5,
                      lineHeight: 1.2
                    }}
                  >
                    {shock.period}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '12px',
                      color: 'text.primary',
                      lineHeight: 1.4
                    }}
                  >
                    {shock.event}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      ) : (
        <Box sx={{ position: 'relative', pl: 3, maxWidth: '800px', margin: '0 auto' }}>
          {/* Vertical timeline line */}
          <Box
            sx={{
              position: 'absolute',
              left: '12px',
              top: '8px',
              bottom: '8px',
              width: '2px',
              bgcolor: 'var(--color-primary)',
              opacity: 0.3
            }}
          />
          
          <Stack spacing={3}>
            {sortedShocks.map((shock, index) => (
              <Box
                key={index}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2
                }}
              >
                {/* Timeline dot */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: '-15px',
                    top: '2px',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    bgcolor: 'var(--color-primary)',
                    border: '2px solid',
                    borderColor: 'background.paper',
                    boxShadow: '0 0 0 2px',
                    boxShadowColor: 'var(--color-primary)'
                  }}
                />
                
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '13px',
                      fontWeight: 700,
                      color: 'var(--color-primary)',
                      mb: 0.5,
                      lineHeight: 1.2
                    }}
                  >
                    {shock.period}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '12px',
                      color: 'text.primary',
                      lineHeight: 1.4
                    }}
                  >
                    {shock.event}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

// Context section (footer)
const ContextSection = ({ context, isStacked }) => {
  if (!context) return null;

  const contextItems = [
    { key: 'politics', label: 'Politics', value: context.politics, icon: PoliticsIcon },
    { key: 'economics', label: 'Economics', value: context.economics, icon: EconomicsIcon },
    { key: 'socioCultural', label: 'Socio Cultural', value: context.socioCultural, icon: SocioCulturalIcon },
    { key: 'peaceSecurity', label: 'Peace & Security', value: context.peaceSecurity, icon: PeaceSecurityIcon },
    { key: 'demographics', label: 'Demographics', value: context.demographics, icon: DemographicsIcon },
    { key: 'environment', label: 'Environment', value: context.environment, icon: EnvironmentIcon }
  ].filter(item => item.value);

  if (contextItems.length === 0) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography
        variant="h6"
        sx={{
          fontSize: '16px',
          fontWeight: 700,
          color: 'text.primary',
          textAlign: 'center',
          mb: 3,
          letterSpacing: '0.5px',
          textTransform: 'uppercase'
        }}
      >
        Context
      </Typography>

      {isStacked ? (
        <Stack spacing={2}>
          {contextItems.map((item, index) => (
            <Paper
              key={item.key}
              elevation={1}
              sx={{
                p: 2.5,
                borderRadius: '8px',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <item.icon sx={{ fontSize: '16px', color: 'primary.main' }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontSize: '13px',
                    fontWeight: 700,
                    color: 'var(--color-primary)',
                    textTransform: 'capitalize'
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '11px',
                  color: 'text.secondary',
                  lineHeight: 1.5
                }}
              >
                {item.value}
              </Typography>
            </Paper>
          ))}
        </Stack>
      ) : (
        <Grid container spacing={2}>
          {contextItems.map((item, index) => (
            <Grid item xs={12} sm={6} md={6} lg={4} xl={4} key={item.key}>
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  borderRadius: '8px',
                  height: '100%',
                  minHeight: '120px',
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <item.icon sx={{ fontSize: '18px', color: 'primary.main' }} />
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '13px',
                      fontWeight: 700,
                      color: 'var(--color-primary)',
                      textAlign: 'center',
                      textTransform: 'capitalize'
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '11px',
                    color: 'text.secondary',
                    lineHeight: 1.4,
                    flex: 1
                  }}
                >
                  {item.value}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

// Arrow component
const Arrow = ({ direction = 'down' }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      py: 1,
      position: 'relative'
    }}
  >
    {/* Extended vertical line */}
    <Box
      sx={{
        position: 'absolute',
        width: '2px',
        height: '60px',
        bgcolor: 'var(--color-primary)',
        opacity: 0.3
      }}
    />
    {/* Arrow head */}
    <Box
      sx={{
        width: 0,
        height: 0,
        borderLeft: '10px solid transparent',
        borderRight: '10px solid transparent',
        borderTop: direction === 'down' ? '14px solid' : 'none',
        borderBottom: direction === 'up' ? '14px solid' : 'none',
                          borderTopColor: direction === 'down' ? 'var(--color-primary)' : 'none',
                  borderBottomColor: direction === 'up' ? 'var(--color-primary)' : 'none',
        opacity: 0.8,
        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))',
        zIndex: 1
      }}
    />
  </Box>
);

// Main Humanitarian Framework Visualization Component
const ProblemTreeVisualization = ({ content }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Use parent width hook
  const [containerRef, isStacked] = useParentWidth(500);

  // Parse the humanitarian framework content
  const parsedData = useMemo(() => {
    if (!content) return null;
    
    try {
      let jsonText = content;
      const codeBlockMatch = content.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
      if (codeBlockMatch) {
        jsonText = codeBlockMatch[1];
      }
      
      const jsonMatch = jsonText.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        jsonText = jsonMatch[0];
      }
      
      const jsonData = JSON.parse(jsonText);
      
      // Check for humanitarian framework structure
      if (jsonData.peopleInNeed || jsonData.priorityGeographicalAreas || jsonData.focalIssues) {
        return jsonData;
      }
    } catch (error) {
      console.log('JSON parsing failed:', error.message);
    }
    
    return null;
  }, [content]);

  const handleToggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  if (!content || !parsedData) {
    return (
      <Box
        sx={{
          p: 6,
          textAlign: 'center',
          border: '2px dashed #e0e0e0',
          borderRadius: '8px'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {content ? 'Unable to parse humanitarian analysis data' : 'No humanitarian analysis content available'}
        </Typography>
      </Box>
    );
  }

  // Defensive check for theme
  if (!theme) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Loading theme...
        </Typography>
      </Box>
    );
  }

  try {
    return (
      <Box
        sx={{
          ...(isFullscreen && {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 9999,
            bgcolor: 'background.default',
            p: 2,
            overflow: 'auto'
          }),
          ...(!isFullscreen && {
            position: 'relative',
            width: '100%'
          })
        }}
      >
      {/* Main Analysis Grid with content background and integrated fullscreen button */}
      <Box 
        ref={containerRef}
        sx={{ 
          bgcolor: 'transparent', 
          p: 0, 
          position: 'relative',
          maxWidth: { xs: '100%', sm: '100%', md: '1100px' },
          margin: '0 auto'
        }}
      >
        {/* Fullscreen button in top right of content area */}
        <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
          <IconButton
            onClick={handleToggleFullscreen}
            sx={{
              position: 'absolute',
              top: isFullscreen ? 16 : 8,
              right: isFullscreen ? 16 : 8,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              zIndex: 10,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              '&:hover': {
                bgcolor: 'grey.50'
              }
            }}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>

        {/* People in Need Header */}
        <PeopleInNeedHeader peopleInNeed={parsedData.peopleInNeed} />

        {/* Priority Sections (Top Row) */}
        <Grid container spacing={2} sx={{ mt: 2 }} direction={isStacked ? 'column' : 'row'}>
          <Grid item xs={12} sm={12} md={4} xl={4}>
            <PrioritySection
              title="Priority geographical areas"
              items={parsedData.priorityGeographicalAreas || []}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4} xl={4}>
            <PrioritySection
              title="Priority groups with specific needs"
              items={parsedData.priorityGroupsWithSpecificNeeds || []}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4} xl={4}>
            <PrioritySection
              title="Priority groups at risk"
              items={parsedData.priorityGroupsAtRisk || []}
            />
          </Grid>
        </Grid>

        {/* Arrow */}
        <Arrow direction="up" />

        {/* Living Conditions Sections (Middle Row) */}
        <Grid container spacing={2} direction={isStacked ? 'column' : 'row'}>
          <Grid item xs={12} sm={12} md={4} xl={4}>
            <LivingConditionSection
              title="Living standards"
              subtitle="(Ability to meet basic needs)"
              items={parsedData.livingStandards?.items || []}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4} xl={4}>
            <LivingConditionSection
              title="Coping Mechanisms"
              subtitle="(Ability to cope with unmet needs)"
              items={parsedData.copingMechanisms?.items || []}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4} xl={4}>
            <LivingConditionSection
              title="Physical & mental wellbeing"
              subtitle="(Ability to maintain wellbeing)"
              items={parsedData.physicalMentalWellbeing?.items || []}
            />
          </Grid>
        </Grid>

        {/* Arrow */}
        <Arrow direction="up" />

        {/* Focal Issues (Central) */}
        <FocalIssuesSection focalIssues={parsedData.focalIssues} />

        {/* Arrow */}
        <Arrow direction="up" />

        {/* Impact Sections (Bottom Row) */}
        <Grid container spacing={2} direction={isStacked ? 'column' : 'row'}>
          <Grid item xs={12} sm={12} md={4} xl={4}>
            <ImpactSection
              title="Impact on people"
              items={parsedData.impactOnPeople || []}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4} xl={4}>
            <ImpactSection
              title="Impact on systems & services"
              items={parsedData.impactOnSystems || []}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4} xl={4}>
            <ImpactSection
              title="Humanitarian access"
              items={parsedData.humanitarianAccess || []}
            />
          </Grid>
        </Grid>

        {/* Arrow */}
        <Arrow direction="up" />

        {/* Timeline Section */}
        <TimelineSection mainShocks={parsedData.mainShocks} isStacked={isStacked} />

        {/* Context Section */}
        <ContextSection context={parsedData.context} isStacked={isStacked} />
      </Box>
    </Box>
  );
  } catch (error) {
    console.error('Error rendering ProblemTreeVisualization:', error);
    return (
      <Box sx={{ p: 2, textAlign: 'center', border: '1px solid red', borderRadius: 1 }}>
        <Typography variant="body2" color="error">
          Error rendering visualization: {error.message}
        </Typography>
      </Box>
    );
  }
};

export default ProblemTreeVisualization; 